'use client';

import { motion } from 'motion/react';
import { useRef, useState } from 'react';
import type { Data } from '@/types/chart';
import DataPointList from './modules/DataPointList';

interface VisualHeaderProps {
  title: string;
  subtitle?: string;
  latestDataPoints: Data[] | null;
  showLatestDataPoints: boolean;
  unit?: string;
}

export function VisualHeader({ title, subtitle, showLatestDataPoints, latestDataPoints, unit }: VisualHeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const currentVariant = isOpen ? 'open' : 'closed';

  const ref = useRef<HTMLDivElement>(null);
  return (
    // biome-ignore lint/a11y/useSemanticElements: Surrounding div that is clickable surface
    <div
      role='button'
      tabIndex={0}
      className='mobile-xs:flex-col mobile-xs:gap-2 relative flex cursor-pointer items-start justify-between gap-4 px-5
        pt-5 pb-5'
      onClick={() => setIsOpen(!isOpen)}
      onKeyUp={() => setIsOpen(!isOpen)}
    >
      <motion.div
        className='group relative flex w-full cursor-pointer items-start justify-between overflow-clip print:h-auto'
        initial={currentVariant}
        animate={currentVariant}
        variants={{
          open: {
            height: 'auto',
          },
          closed: {
            height: 66,
          },
        }}
      >
        <div ref={ref}>
          <h3 className='text-primary text-base font-semibold text-balance'>{title}</h3>
          <span className='text-muted-foreground mobile:mb-0 mt-0.5 mb-5 block text-wrap'>{subtitle}</span>

          {(ref?.current?.clientHeight ?? 0) > 86 && (
            <div
              aria-hidden
              className='from-background mobile:hidden absolute bottom-0 left-0 z-10 h-3 w-full bg-linear-to-t
                to-transparent transition-opacity duration-300 group-active:opacity-0'
            />
          )}
        </div>
        <div className='mt-0.5 mb-5 flex flex-col gap-2'>
          {showLatestDataPoints && (
            <DataPointList
              latestDataPoints={latestDataPoints}
              unit={unit}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
}
